import type { MedplumClient } from '@medplum/core'
import { isNil } from 'lodash'
import type { Patient } from '@medplum/fhirtypes'
import {
  Get_PatientQuery,
  GetPatientDataQuery,
  PatientData_GetPatientQuery,
  PatientData_GetPatientsByIdsQuery,
  PatientData_PatientCareDataFragment,
  UsePatientChartQuery
} from 'generated/graphql'
import { GET_PATIENT } from 'components/Patients/Patient/getPatient.query'
import { GET_PATIENT_NAMES } from 'components/CreateProvider/getPatientNames.query'
import { GET_PATIENT_DATA } from 'hooks/usePatientData.query'
import { GET_PATIENT_CHART } from 'hooks/usePatientChart.query'
import { GET_PATIENT_DATA_PATIENT, GET_PATIENTS_BY_IDS } from 'store/queries/patient.query'
import { ConsentCategoryCode } from 'fhir/Consent/constants'
import { BASE_API_URL } from 'utils/constants'
import { HttpClient, HttpResponseError } from 'utils/httpClient'
import { ALLOWED_STATUS_CODES, medplum as defaultMedplum } from 'utils/medplumClient'
import { captureException, SentrySources } from 'utils/sentry'

/**
 * Response shape from GET /patients/{id} (replaces GET_PATIENT_DATA).
 */
export interface GetPatientDataResponse {
  Patient: Record<string, unknown>
}

/** Response shape from POST /patients/by-ids (replaces GET_PATIENTS_BY_IDS GraphQL). */
export interface GetPatientsByIdsResponse {
  PatientList: Record<string, unknown>[]
}

export interface GetPatientDataOptions {
  includesAll?: boolean
  includes?: string[]
  /** Consent statuses to include in ConsentList (e.g. active, proposed). Omit for API default. */
  consentStatuses?: string[]
  /** EpisodeOfCare statuses to include in EpisodeOfCareList (e.g. active, planned). Omit for API default. */
  episodeOfCareStatuses?: string[]
  /** When Consent is included, restrict ConsentList to these category codes (e.g. authorization). */
  consentCategories?: string[]
  /**
   * When Consent is included, whether to GET each Consent.provision.actor reference missing from the bundle.
   * Chart-style reads use false to match GET_PATIENT_CHART GraphQL behavior.
   */
  fetchMissingConsentActors?: boolean
}

/** Mirrored from `CLINICAL_CHART_INCLUDES` in apps/api/services/domain/patient_data/service.py */
export const CLINICAL_CHART_INCLUDES = [
  'GeneralPractitioner',
  'Condition',
  'Device',
  'Coverage',
  'Consent'
] as const

/**
 * Fetch patient data from REST API GET /patients/{id}.
 * @param patientId - The id of the patient
 * @param options - Optional includes_all (default true) and includes list
 * @returns The patient data or null if not found
 */
export async function getPatientData(
  patientId: string,
  options: GetPatientDataOptions = {}
): Promise<GetPatientDataResponse | null> {
  const {
    includesAll = true,
    includes,
    consentStatuses,
    episodeOfCareStatuses,
    consentCategories,
    fetchMissingConsentActors
  } = options
  const params = new URLSearchParams()
  params.set('includes_all', String(includesAll))
  if (!includesAll && includes?.length) {
    includes.forEach((inc) => params.append('includes', inc))
  }
  if (consentStatuses?.length) {
    consentStatuses.forEach((s) => params.append('consent_statuses', s))
  }
  if (episodeOfCareStatuses?.length) {
    episodeOfCareStatuses.forEach((s) => params.append('episode_of_care_statuses', s))
  }
  if (consentCategories?.length) {
    consentCategories.forEach((c) => params.append('consent_categories', c))
  }
  if (fetchMissingConsentActors !== undefined) {
    params.set('fetch_missing_consent_actors', String(fetchMissingConsentActors))
  }
  const query = params.toString()
  const url = `${BASE_API_URL}/patients/${patientId}${query ? `?${query}` : ''}`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    if (response.status === 404) return null
    throw new Error(`Failed to fetch patient data: ${response.status}`)
  }
  return (await response.json()) as GetPatientDataResponse
}

/**
 * Fetch multiple patients' care data from POST /patients/by-ids (GraphQL PatientData_GetPatientsByIds parity).
 * For raw FHIR Patient resources via search, use {@link getPatientsByIds} instead.
 */
export async function getPatientCareDataByIds(
  ids: string[]
): Promise<GetPatientsByIdsResponse | null> {
  const unique = Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)))
  if (unique.length === 0) {
    return { PatientList: [] }
  }
  const response = await new HttpClient().fetch(`${BASE_API_URL}/patients/by-ids`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids: unique })
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch patients by ids: ${response.status}`)
  }
  return (await response.json()) as GetPatientsByIdsResponse
}

/**
 * Keys to ignore when comparing GraphQL vs REST patient payloads for parity checks.
 * - __typename: Apollo/GraphQL client metadata, not present on REST.
 * - conditions: still requested on the GraphQL patient query, but nothing in the app consumes it;
 *   we skip it here so GraphQL vs API diffs are not noise from that unused field.
 * - CareTeamList[i].managingOrganization: skipped via path-aware check (see
 *   isIgnoredCareTeamListManagingOrganizationField); not used in UI and shapes differ by stack.
 */
const IGNORED_KEYS = new Set(['__typename', 'conditions'])

/** For GET_PATIENT_CHART vs REST chart includes: compare `conditions` (GraphQL uses ConditionList, normalized before compare). */
const CHART_PARITY_IGNORED_KEYS = new Set(['__typename'])

/** Ignore discrepancy when the field is "id" and GraphQL has null (API may populate id from reference). */
function isIgnoredIdDiscrepancy(path: string, graphqlValue: unknown): boolean {
  return path.endsWith('.id') && isNil(graphqlValue)
}

/** True when val is a prototype object (e.g. Object.prototype); ignore such GraphQL values in discrepancy check. */
function isPrototype(val: unknown): boolean {
  if (val === null || typeof val !== 'object') return false
  // Built-in prototypes, any object that is the prototype of its constructor, or has no prototype (root prototype)
  return (
    val === Object.prototype ||
    val === Array.prototype ||
    Object.getPrototypeOf(val) === null ||
    (val as object).constructor?.prototype === val
  )
}

/**
 * Keys present in the REST response that are structural/raw FHIR fields not present in the
 * GraphQL response (which transforms them into named fields). Excluded from shape comparison.
 */
const REST_STRUCTURAL_KEYS = new Set(['meta', 'extension', 'type'])
const PATIENT_ROOT_PATH = /^Patient(?:\[[^\]]+\])?$/

/** Deterministic key for sorting array elements so list order does not cause false discrepancies. */
function canonicalSortKey(x: unknown): string {
  if (x === null) return 'null'
  if (x === undefined) return 'undefined'
  if (typeof x !== 'object') return String(x)
  if (Array.isArray(x)) {
    return '[' + x.map(canonicalSortKey).join(',') + ']'
  }
  const obj = x as Record<string, unknown>
  const keys = Object.keys(obj)
    .filter((k) => !IGNORED_KEYS.has(k))
    .sort()
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalSortKey(obj[k])).join(',') + '}'
}

/**
 * Sort key for FHIR Identifier-like objects (nested resources, etc.) so identifier arrays match
 * regardless of order. Uses system then value.
 */
function identifierSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const system = !isNil(obj.system) ? String(obj.system) : ''
  const value = !isNil(obj.value) ? String(obj.value) : ''
  return `${system}|${value}`
}

/** True for Patient.identifier on the patient root (not e.g. Patient.contact[].identifier). */
function isPatientRootIdentifierPath(path: string): boolean {
  if (path === 'Patient.identifier') return true
  return /^Patient\[[^\]]+\]\.identifier$/.test(path)
}

/**
 * Sort Patient.identifier by value (then system) so GraphQL vs REST order differences don't
 * false-positive; matches how clinicians expect to align rows by business identifier value.
 */
function patientIdentifierSortByValueKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const value = !isNil(obj.value) ? String(obj.value) : ''
  const system = !isNil(obj.system) ? String(obj.system) : ''
  return `${value}|${system}`
}

/** Sort key for FHIR Consent-like objects so ConsentList arrays match regardless of order. */
function consentListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const id = !isNil(obj.id) ? String(obj.id) : ''
  const dateTime = !isNil(obj.dateTime) ? String(obj.dateTime) : ''
  const status = !isNil(obj.status) ? String(obj.status) : ''
  return `${id}|${dateTime}|${status}`
}

/** Sort key for EpisodeOfCareList by id so array order does not cause false discrepancies. */
function episodeOfCareListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  return !isNil(obj.id) ? String(obj.id) : ''
}

/** Sort key for CarePlanList by id so array order does not cause false discrepancies. */
function carePlanListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  return !isNil(obj.id) ? String(obj.id) : ''
}

/** Sort key for PractitionerRoleList by id so array order does not cause false discrepancies. */
function practitionerRoleListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  return !isNil(obj.id) ? String(obj.id) : ''
}

/** Sort key for CoverageList by id so array order does not cause false discrepancies. */
function coverageListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  return !isNil(obj.id) ? String(obj.id) : ''
}

/** Sort key for CareTeamList by id so array order does not cause false discrepancies. */
function careTeamListSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  return !isNil(obj.id) ? String(obj.id) : ''
}

/** Sort key for Patient.extension by url so array order does not cause false discrepancies. */
function extensionSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const url = !isNil(obj.url) ? String(obj.url) : ''
  return url
}

/** Get reference string from a performer item (reference may be string or object). */
function getReferenceSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const ref = obj.reference
  if (typeof ref === 'string') return ref
  if (ref !== null && ref !== undefined && typeof ref === 'object' && 'reference' in ref)
    return String((ref as { reference?: string }).reference ?? '')
  return ''
}

/** Sort key for Consent.provision.actor[] by reference.reference so array order does not cause false discrepancies. */
function provisionActorSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const ref = obj.reference
  if (typeof ref === 'string') return ref
  if (!isNil(ref) && typeof ref === 'object') {
    const inner = (ref as { reference?: string }).reference
    return !isNil(inner) ? String(inner) : ''
  }
  return ''
}

/** Dedupe array by key; keeps first occurrence per key. */
function dedupeByKey(arr: unknown[], keyFn: (x: unknown) => string): unknown[] {
  const seen = new Set<string>()
  return arr.filter((item) => {
    const key = keyFn(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * Sort key for CareTeam participant arrays by member.reference so order doesn't cause
 * false discrepancies. GraphQL and REST return participants in different order, and
 * canonicalSortKey fails here because the nested Practitioner objects differ in shape
 * between the two responses — sorting by member.reference gives a stable identity key.
 */
function participantSortKey(x: unknown): string {
  if (x === null || x === undefined) return String(x)
  if (typeof x !== 'object') return String(x)
  const obj = x as Record<string, unknown>
  const member = obj.member as Record<string, unknown> | null | undefined
  if (member && typeof member === 'object') {
    const ref = member.reference
    if (!isNil(ref)) return String(ref)
    const id = member.id
    if (!isNil(id)) return String(id)
  }
  return canonicalSortKey(x)
}

export interface PatientDataDiscrepancy {
  path: string
  graphqlValue: unknown
  apiValue: unknown
}

/**
 * Skip CareTeam.managingOrganization under Patient.CareTeamList[i] — not used in the UI;
 * GraphQL vs REST embed/resolve organization on care teams differently.
 */
function isIgnoredCareTeamListManagingOrganizationField(parentPath: string, key: string): boolean {
  return key === 'managingOrganization' && /CareTeamList\[\d+\]$/.test(parentPath)
}

/** True when path is Period start or end (so we compare as datetime). */
function isPeriodStartOrEndPath(path: string): boolean {
  return path.endsWith('.period.start') || path.endsWith('.period.end')
}

/** Parse value as ISO date/datetime; return ms since epoch or null if unparseable. */
function parsePeriodDateTime(value: unknown): number | null {
  if (isNil(value)) return null
  const s = typeof value === 'string' ? value : typeof value === 'number' ? value : null
  if (s === null) return null
  const d = new Date(s as string | number)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

/**
 * Recursively compare GraphQL and API patient payloads and collect discrepancies.
 * Ignores __typename. Logs nothing; returns list of discrepancies.
 *
 * When `restKeysOnly` is true (e.g. profile parity checks), the comparison
 * is scoped to the REST contract:
 *
 *  - REST is a pared-down subset of the full GraphQL response. GraphQL returns many
 *    related resources (Consents, EpisodeOfCare, CarePlans, etc.) and extension-aliased
 *    fields (authorizedLocation1, birthSexExtension, etc.) that REST doesn't request
 *    for the list endpoint. The backend sets non-requested list keys to null via
 *    _ensure_patient_care_data_shape, and extension-aliased keys are absent entirely.
 *
 *  - GraphQL is the source of truth for values, but only for the fields REST populates.
 *    We iterate REST keys only, skip null-valued REST keys (meaning "not requested"),
 *    and skip fields where GraphQL is null/undefined (meaning "not queried by the
 *    GraphQL fragment", e.g. address.use). A discrepancy is only flagged when both
 *    sides have a value and they disagree. REST may return longer arrays or extra nested
 *    values (e.g. more PractitionerRole rows); those are not discrepancies.
 */
function comparePatientDataPayloads(
  graphqlVal: unknown,
  apiVal: unknown,
  path: string,
  restKeysOnly = false,
  ignoredKeys: ReadonlySet<string> = IGNORED_KEYS
): PatientDataDiscrepancy[] {
  const discrepancies: PatientDataDiscrepancy[] = []

  if (graphqlVal === apiVal) return discrepancies
  if (isPrototype(graphqlVal)) return discrepancies
  if (isNil(graphqlVal) && isNil(apiVal)) return discrepancies
  // REST returns raw FHIR fields (e.g. address.use) that GraphQL fragments don't query.
  // A null/undefined graphqlVal means "not queried" / omitted — do not flag when REST has
  // a value anyway (extra array elements, extra nested objects). Still flag when REST is
  // missing data GraphQL has (graphql present, api null).
  if (restKeysOnly && isNil(graphqlVal) && !isNil(apiVal)) return discrepancies
  if (isNil(graphqlVal) || isNil(apiVal)) {
    if (!isPrototype(graphqlVal))
      discrepancies.push({ path, graphqlValue: graphqlVal, apiValue: apiVal })
    return discrepancies
  }

  if (typeof graphqlVal !== typeof apiVal) {
    if (!isPrototype(graphqlVal))
      discrepancies.push({ path, graphqlValue: graphqlVal, apiValue: apiVal })
    return discrepancies
  }

  if (typeof graphqlVal !== 'object' || typeof apiVal !== 'object') {
    if (graphqlVal !== apiVal && !isPrototype(graphqlVal)) {
      if (isPeriodStartOrEndPath(path)) {
        const t1 = parsePeriodDateTime(graphqlVal)
        const t2 = parsePeriodDateTime(apiVal)
        if (!isNil(t1) && !isNil(t2) && t1 === t2) {
          return discrepancies
        }
      }
      discrepancies.push({ path, graphqlValue: graphqlVal, apiValue: apiVal })
    }
    return discrepancies
  }

  const g = graphqlVal as Record<string, unknown>
  const a = apiVal as Record<string, unknown>

  if (Array.isArray(g) && Array.isArray(a)) {
    const isPatientIdentifierArray = isPatientRootIdentifierPath(path)
    const isIdentifierArray = path.endsWith('.identifier')
    const isConsentListArray = path.endsWith('.ConsentList')
    const isEpisodeOfCareListArray = path.includes('EpisodeOfCareList')
    const isCarePlanListArray = path.endsWith('.CarePlanList')
    const isCoverageListArray = path.endsWith('.CoverageList')
    const isConditionsArray = path.endsWith('.conditions')
    const isCareTeamListArray = path.endsWith('.CareTeamList')
    const isExtensionArray = path.endsWith('.extension')
    const isPerformerArray = path.endsWith('.performer')
    const isProvisionActorArray = path.endsWith('.provision.actor')
    const isPractitionerRoleListArray = path.endsWith('.PractitionerRoleList')
    const isParticipantArray = path.endsWith('.participant')
    const keyFn = isPatientIdentifierArray
      ? patientIdentifierSortByValueKey
      : isIdentifierArray
      ? identifierSortKey
      : isConsentListArray
      ? consentListSortKey
      : isEpisodeOfCareListArray
      ? episodeOfCareListSortKey
      : isCarePlanListArray
      ? carePlanListSortKey
      : isCoverageListArray
      ? coverageListSortKey
      : isConditionsArray
      ? episodeOfCareListSortKey
      : isCareTeamListArray
      ? careTeamListSortKey
      : isExtensionArray
      ? extensionSortKey
      : isProvisionActorArray
      ? provisionActorSortKey
      : isPerformerArray
      ? getReferenceSortKey
      : isPractitionerRoleListArray
      ? practitionerRoleListSortKey
      : isParticipantArray
      ? participantSortKey
      : canonicalSortKey
    const sortByKey = (u: unknown, v: unknown): number => keyFn(u).localeCompare(keyFn(v))
    const graphqlArr = isPractitionerRoleListArray
      ? dedupeByKey([...g], practitionerRoleListSortKey)
      : g
    const sortedG = [...graphqlArr].sort(sortByKey)
    const sortedA = [...a].sort(sortByKey)
    const maxLen = Math.max(sortedG.length, sortedA.length)
    for (let i = 0; i < maxLen; i++) {
      const childDiscrepancies = comparePatientDataPayloads(
        sortedG[i],
        sortedA[i],
        path ? `${path}[${i}]` : `[${i}]`,
        restKeysOnly,
        ignoredKeys
      )
      discrepancies.push(
        ...childDiscrepancies.filter(
          (d) => !isPrototype(d.graphqlValue) && !isIgnoredIdDiscrepancy(d.path, d.graphqlValue)
        )
      )
    }
    return discrepancies
  }

  if (Array.isArray(g) !== Array.isArray(a)) {
    if (!isPrototype(graphqlVal) && !isIgnoredIdDiscrepancy(path, graphqlVal))
      discrepancies.push({ path, graphqlValue: graphqlVal, apiValue: apiVal })
    return discrepancies
  }

  // In restKeysOnly mode, only walk REST keys — GraphQL-only keys are extra data not
  // consumed by REST-first callers and should not trigger alerts.
  const keysToCompare = restKeysOnly
    ? Object.keys(a)
    : Array.from(new Set([...Object.keys(g), ...Object.keys(a)]))
  for (const key of keysToCompare) {
    if (ignoredKeys.has(key)) continue
    if (isIgnoredCareTeamListManagingOrganizationField(path, key)) continue
    if (restKeysOnly && PATIENT_ROOT_PATH.test(path) && REST_STRUCTURAL_KEYS.has(key)) continue
    // Null REST value means the backend didn't populate this field (non-requested include
    // set to None by _ensure_patient_care_data_shape) — not a meaningful comparison.
    if (restKeysOnly && isNil(a[key])) continue
    const childPath = path ? `${path}.${key}` : key
    const childDiscrepancies = comparePatientDataPayloads(
      g[key],
      a[key],
      childPath,
      restKeysOnly,
      ignoredKeys
    )
    discrepancies.push(
      ...childDiscrepancies.filter(
        (d) => !isPrototype(d.graphqlValue) && !isIgnoredIdDiscrepancy(d.path, d.graphqlValue)
      )
    )
  }
  return discrepancies
}

/**
 * Compare GraphQL and API patient data responses and log an error for each discrepancy.
 * Call this when both responses are available (e.g. after dual fetch).
 */
function sortConsentListByKey(list: unknown[]): unknown[] {
  return [...list].sort((u, v) => consentListSortKey(u).localeCompare(consentListSortKey(v)))
}

function sortEpisodeOfCareListById(list: unknown[]): unknown[] {
  return [...list].sort((u, v) =>
    episodeOfCareListSortKey(u).localeCompare(episodeOfCareListSortKey(v))
  )
}

function withSortedEpisodeOfCareList(
  patient: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (isNil(patient) || !Array.isArray(patient.EpisodeOfCareList)) return patient
  return {
    ...patient,
    EpisodeOfCareList: sortEpisodeOfCareListById(patient.EpisodeOfCareList as unknown[])
  }
}

export function logPatientDataDiscrepancies(
  patientId: string,
  graphqlData: GetPatientDataQuery,
  apiData: GetPatientDataResponse,
  restKeysOnly = false
): boolean {
  const rawGraphqlPatient = graphqlData?.Patient ?? null
  const rawApiPatient = apiData?.Patient ?? null
  const graphqlPatient =
    !isNil(rawGraphqlPatient) &&
    typeof rawGraphqlPatient === 'object' &&
    Array.isArray((rawGraphqlPatient as Record<string, unknown>).ConsentList)
      ? {
          ...(rawGraphqlPatient as Record<string, unknown>),
          ConsentList: sortConsentListByKey(
            (rawGraphqlPatient as Record<string, unknown>).ConsentList as unknown[]
          )
        }
      : rawGraphqlPatient
  const apiPatient = withSortedEpisodeOfCareList(
    !isNil(rawApiPatient) && typeof rawApiPatient === 'object'
      ? (rawApiPatient as Record<string, unknown>)
      : null
  )
  const graphqlPatientSortedEoc =
    !isNil(graphqlPatient) && typeof graphqlPatient === 'object'
      ? withSortedEpisodeOfCareList(graphqlPatient as Record<string, unknown>)
      : graphqlPatient
  const discrepancies = comparePatientDataPayloads(
    graphqlPatientSortedEoc,
    apiPatient,
    'Patient',
    restKeysOnly
  )
  if (discrepancies.length === 0) {
    return false
  }
  const extraFields = discrepancies.filter(
    (d) =>
      (d.graphqlValue === null || d.graphqlValue === undefined) &&
      d.apiValue !== null &&
      d.apiValue !== undefined
  )
  const discrepanciesExcludingExtraFields = discrepancies.filter(
    (d) => !(isNil(d.graphqlValue) && !isNil(d.apiValue))
  )
  // Do not raise when the only discrepancies are extra fields (API has field, GraphQL doesn't).
  if (discrepanciesExcludingExtraFields.length === 0) {
    return false
  }
  const message = `Patient data discrepancy (patientId=${patientId}): ${discrepanciesExcludingExtraFields.length} difference(s)`
  captureException(new Error(message), {
    tags: { source: SentrySources.PATIENT_SLICE },
    extras: {
      info: {
        patientId,
        discrepancyCount: discrepanciesExcludingExtraFields.length,
        discrepancies: discrepanciesExcludingExtraFields.map((d) => ({
          path: d.path,
          graphqlValue: d.graphqlValue,
          apiValue: d.apiValue
        })),
        extraFields: extraFields.map((d) => ({ path: d.path, apiValue: d.apiValue })),
        graphqlPatient: graphqlPatientSortedEoc,
        apiPatient
      }
    }
  })
  return true
}

function patientCareDataListById(
  list: Array<Record<string, unknown> | null | undefined> | null | undefined
): Map<string, Record<string, unknown>> {
  const m = new Map<string, Record<string, unknown>>()
  if (!list?.length) return m
  for (const p of list) {
    if (p && typeof p === 'object' && typeof p.id === 'string') {
      m.set(p.id, p)
    }
  }
  return m
}

/**
 * Compare GraphQL vs REST batch patient payloads (GET_PATIENTS_BY_IDS vs POST /patients/by-ids).
 * Logs per-patient discrepancies and mismatched presence when a requested id exists on only one side.
 */
export function logPatientsByIdsDiscrepancies(
  requestedIds: string[],
  graphqlList: PatientData_GetPatientsByIdsQuery['PatientList'] | null | undefined,
  apiList: Record<string, unknown>[] | null | undefined
): void {
  const gMap = patientCareDataListById(
    graphqlList as Array<Record<string, unknown> | null | undefined>
  )
  const aMap = patientCareDataListById(apiList)
  for (const id of requestedIds) {
    const g = gMap.get(id)
    const a = aMap.get(id)
    if (!g && !a) continue
    if (!g || !a) {
      captureException(new Error(`Patients by ids presence mismatch (patientId=${id})`), {
        tags: { source: SentrySources.PATIENT_SLICE },
        extras: {
          info: { patientId: id, hasGraphql: Boolean(g), hasApi: Boolean(a) }
        }
      })
      continue
    }
    logPatientDataDiscrepancies(id, { Patient: g } as GetPatientDataQuery, { Patient: a })
  }
}

/**
 * Fetch patients by ids via GraphQL (GET_PATIENTS_BY_IDS). When useApi is true, also calls
 * POST /patients/by-ids in parallel, compares payloads, and returns the REST result.
 */
export async function fetchPatientsByIds(
  medplum: MedplumClient,
  ids: string[],
  useApi: boolean
): Promise<PatientData_GetPatientsByIdsQuery> {
  const uniqueIds = Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)))
  const idsParam = uniqueIds.join(',')

  const graphqlPromise =
    uniqueIds.length === 0
      ? Promise.resolve({
          data: { PatientList: [] } as PatientData_GetPatientsByIdsQuery,
          error: undefined
        })
      : medplum.graphql(GET_PATIENTS_BY_IDS, null, { ids: idsParam })

  const apiPromise =
    useApi && uniqueIds.length > 0
      ? getPatientCareDataByIds(uniqueIds).catch((err) => {
          captureException(normalizeError(err), {
            tags: { source: SentrySources.PATIENT_FETCH },
            extras: { params: { idCount: uniqueIds.length } }
          })
          return null
        })
      : Promise.resolve(null)

  const [{ data, error }, apiResponse] = await Promise.all([graphqlPromise, apiPromise])

  if (error) {
    throw new Error('Failed to query patients by ids')
  }
  const graphqlResult = data as PatientData_GetPatientsByIdsQuery

  if (useApi && !isNil(apiResponse) && !isNil(graphqlResult?.PatientList)) {
    logPatientsByIdsDiscrepancies(
      uniqueIds,
      graphqlResult.PatientList,
      apiResponse.PatientList as Record<string, unknown>[]
    )
  }

  if (useApi && !isNil(apiResponse)) {
    return {
      PatientList: apiResponse.PatientList as PatientData_GetPatientsByIdsQuery['PatientList']
    }
  }
  return graphqlResult
}

/**
 * GraphQL chart query uses `ConditionList`; REST uses `conditions`. Align for comparison.
 */
function normalizeGraphqlChartPatientForCompare(
  patient: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (isNil(patient)) return null
  const next: Record<string, unknown> = { ...patient }
  if (Array.isArray(next.ConditionList)) {
    next.conditions = next.ConditionList
  }
  delete next.ConditionList
  return next
}

/**
 * Map REST chart patient to the GraphQL `UsePatientChart` shape (ConditionList from conditions).
 */
function mapRestChartPatientToGraphqlChartPatient(
  patient: Record<string, unknown> | null | undefined
): UsePatientChartQuery['Patient'] | undefined {
  if (isNil(patient) || typeof patient !== 'object') return undefined
  const { conditions, ...rest } = patient
  const out: Record<string, unknown> = { ...rest }
  if (conditions !== undefined) {
    out.ConditionList = conditions
  }
  return out as UsePatientChartQuery['Patient']
}

/**
 * Compare GET_PATIENT_CHART GraphQL vs GET /patients/{id} chart-style REST; log discrepancies to Sentry.
 */
export function logPatientChartDiscrepancies(
  patientId: string,
  graphqlData: UsePatientChartQuery,
  apiData: GetPatientDataResponse
): boolean {
  const rawGraphqlPatient = graphqlData?.Patient as Record<string, unknown> | null | undefined
  const rawApiPatient = apiData?.Patient as Record<string, unknown> | null | undefined
  let graphqlPatient = normalizeGraphqlChartPatientForCompare(rawGraphqlPatient ?? null)
  if (
    !isNil(graphqlPatient) &&
    typeof graphqlPatient === 'object' &&
    Array.isArray(graphqlPatient.ConsentList)
  ) {
    graphqlPatient = {
      ...graphqlPatient,
      ConsentList: sortConsentListByKey(graphqlPatient.ConsentList as unknown[])
    }
  }
  const apiPatient = withSortedEpisodeOfCareList(
    !isNil(rawApiPatient) && typeof rawApiPatient === 'object' ? rawApiPatient : null
  )
  const graphqlPatientSortedEoc =
    !isNil(graphqlPatient) && typeof graphqlPatient === 'object'
      ? withSortedEpisodeOfCareList(graphqlPatient)
      : graphqlPatient
  const discrepancies = comparePatientDataPayloads(
    graphqlPatientSortedEoc,
    apiPatient,
    'Patient',
    false,
    CHART_PARITY_IGNORED_KEYS
  )
  if (discrepancies.length === 0) {
    return false
  }
  const extraFields = discrepancies.filter(
    (d) =>
      (d.graphqlValue === null || d.graphqlValue === undefined) &&
      d.apiValue !== null &&
      d.apiValue !== undefined
  )
  const discrepanciesExcludingExtraFields = discrepancies.filter(
    (d) => !(isNil(d.graphqlValue) && !isNil(d.apiValue))
  )
  if (discrepanciesExcludingExtraFields.length === 0) {
    return false
  }
  const message = `Patient chart discrepancy (patientId=${patientId}): ${discrepanciesExcludingExtraFields.length} difference(s)`
  captureException(new Error(message), {
    tags: { source: SentrySources.PATIENT_CHART },
    extras: {
      info: {
        patientId,
        discrepancyCount: discrepanciesExcludingExtraFields.length,
        discrepancies: discrepanciesExcludingExtraFields.map((d) => ({
          path: d.path,
          graphqlValue: d.graphqlValue,
          apiValue: d.apiValue
        })),
        extraFields: extraFields.map((d) => ({ path: d.path, apiValue: d.apiValue })),
        graphqlPatient: graphqlPatientSortedEoc,
        apiPatient
      }
    }
  })
  return true
}

/**
 * Fetch patient chart via GraphQL (GET_PATIENT_CHART). When useApi is true, also calls
 * GET /patients/{id} with chart includes in parallel, compares responses and logs discrepancies,
 * and returns the API-mapped response. When useApi is false, returns the GraphQL result.
 */
export async function fetchPatientChart(
  medplum: MedplumClient,
  patientId: string,
  useApi: boolean
): Promise<UsePatientChartQuery> {
  const graphqlPromise = medplum.graphql(GET_PATIENT_CHART, null, { patientId })
  const apiPromise = useApi
    ? getPatientData(patientId, {
        includesAll: false,
        includes: [...CLINICAL_CHART_INCLUDES],
        consentCategories: [ConsentCategoryCode.AUTHORIZATION],
        fetchMissingConsentActors: false
      }).catch(() => null)
    : Promise.resolve(null)
  const [{ data, error }, apiResponse] = await Promise.all([graphqlPromise, apiPromise])
  if (error) {
    throw new Error('Failed to query patient chart')
  }
  const graphqlResult = data as UsePatientChartQuery
  if (useApi && !isNil(apiResponse?.Patient) && !isNil(graphqlResult?.Patient)) {
    logPatientChartDiscrepancies(patientId, graphqlResult, apiResponse)
  }
  if (useApi && !isNil(apiResponse?.Patient)) {
    return {
      Patient: mapRestChartPatientToGraphqlChartPatient(
        apiResponse.Patient as Record<string, unknown>
      )
    } as UsePatientChartQuery
  }
  return graphqlResult
}

/**
 * Fetch patient data via GraphQL (GET_PATIENT_DATA). When useApi is true, also calls
 * GET /patients/{id} in parallel (dual fetch), compares responses and logs any discrepancies,
 * and returns the API response. When useApi is false, returns the GraphQL result.
 */
export async function fetchPatientData(
  medplum: MedplumClient,
  patientId: string,
  useApi: boolean
): Promise<GetPatientDataQuery> {
  const graphqlPromise = medplum.graphql(GET_PATIENT_DATA, null, {
    patientId
  })
  const apiPromise = useApi
    ? getPatientData(patientId, { includesAll: false, includes: ['Consent', 'EpisodeOfCare'] })
    : Promise.resolve(null)
  const [{ data, error }, apiResponse] = await Promise.all([
    graphqlPromise,
    apiPromise.catch(() => null)
  ])
  if (error) {
    throw new Error('Failed to query patient data')
  }
  const graphqlResult = data as GetPatientDataQuery
  if (useApi && !isNil(apiResponse?.Patient) && !isNil(graphqlResult?.Patient)) {
    logPatientDataDiscrepancies(patientId, graphqlResult, apiResponse)
  }
  if (useApi && !isNil(apiResponse)) {
    return { Patient: apiResponse.Patient } as GetPatientDataQuery
  }
  return graphqlResult
}

/**
 * Fetch single patient via GraphQL (GET_PATIENT_DATA_PATIENT). When useApi is true, also calls
 * GET /patients/{id} in parallel (dual fetch), compares responses and logs any discrepancies,
 * and returns the API response. When useApi is false, returns the GraphQL result.
 */
export async function fetchPatientDataPatient(
  medplum: MedplumClient,
  patientId: string,
  useApi: boolean,
  consentStatusFilter?: string
): Promise<PatientData_GetPatientQuery> {
  const graphqlPromise = medplum.graphql(GET_PATIENT_DATA_PATIENT, null, {
    id: patientId,
    ...(!isNil(consentStatusFilter) && { consentStatusFilter })
  })
  const apiPromise = useApi
    ? getPatientData(patientId, {
        includesAll: true,
        consentStatuses: ['active', 'proposed'],
        episodeOfCareStatuses: ['active', 'planned']
      })
    : Promise.resolve(null)
  const [{ data, error }, apiResponse] = await Promise.all([
    graphqlPromise,
    apiPromise.catch(() => null)
  ])
  if (error) {
    throw new Error('Failed to query patient data')
  }
  const graphqlResult = data as PatientData_GetPatientQuery
  if (useApi && !isNil(apiResponse?.Patient) && !isNil(graphqlResult?.Patient)) {
    logPatientDataDiscrepancies(
      patientId,
      graphqlResult as unknown as GetPatientDataQuery,
      apiResponse
    )
  }
  if (useApi && !isNil(apiResponse)) {
    return { Patient: apiResponse.Patient } as PatientData_GetPatientQuery
  }
  return graphqlResult
}

/**
 * Dual-fetch for GET_PATIENT: GraphQL baseline + REST GET /patients/{id}?includes=CareTeam&includes=Observations.
 * When useApi=false, returns GraphQL result only. When useApi=true, runs both in parallel,
 * logs discrepancies to Sentry, returns REST result on match, falls back to GraphQL on mismatch/failure.
 */
export async function fetchProfilePatientData(
  medplum: MedplumClient,
  patientId: string,
  useApi: boolean
): Promise<Get_PatientQuery> {
  const graphqlPromise = medplum.graphql(GET_PATIENT, null, { patientId })
  const apiPromise = useApi
    ? getPatientData(patientId, { includesAll: false, includes: ['CareTeam', 'Observations'] })
    : Promise.resolve(null)

  const [{ data, error }, apiResponse] = await Promise.all([
    graphqlPromise,
    apiPromise.catch((err) => {
      const isSilenced =
        err instanceof HttpResponseError && ALLOWED_STATUS_CODES.includes(err.status)
      if (!isSilenced) {
        captureException(normalizeError(err), {
          tags: { source: SentrySources.PATIENT_FETCH },
          extras: { params: { patientId } }
        })
      }
      return null
    })
  ])

  if (error) throw new Error('Failed to query patient profile')

  const graphqlResult = data as Get_PatientQuery

  if (useApi && !isNil(apiResponse?.Patient) && !isNil(graphqlResult?.Patient)) {
    const discrepancies = comparePatientDataPayloads(
      graphqlResult.Patient,
      apiResponse.Patient,
      'Patient',
      true // restKeysOnly: skip REST-only extension keys not queried by GET_PATIENT
    )
    if (discrepancies.length > 0) {
      const message = `Patient profile discrepancy (patientId=${patientId}): ${discrepancies.length} difference(s)`
      captureException(new Error(message), {
        tags: { source: SentrySources.PATIENT_SLICE },
        extras: { info: { patientId, discrepancyCount: discrepancies.length, discrepancies } }
      })
      return graphqlResult
    }
  }

  if (useApi && !isNil(apiResponse)) {
    return { Patient: apiResponse.Patient } as Get_PatientQuery
  }

  return graphqlResult
}

export const DEFAULT_PATIENT_NAME_COUNT = 1000

/** Result for getPatientConnection (POST /patients/search). */
export interface PatientConnectionResult {
  edges: Array<{
    resource: PatientData_PatientCareDataFragment
  }>
  count: number
  /** Set while more patients may match a linkedStatus search. */
  nextCursor?: string | null
}

export interface GetPatientConnectionParams {
  count: number
  offset: number
  name?: string
  addressState?: string[]
  caseManagerId?: string | null
  includeTestIds?: boolean
  linkedStatus?: 'linked' | 'unlinked'
  /** With linkedStatus, the nextCursor from the previous page. */
  cursor?: string
}

export interface GetPatientNamesParams {
  count?: number
  offset?: number
  name?: string
  addressState?: string[]
  caseManagerId?: string | null
  includeTestIds?: boolean
}

interface PatientNameText {
  text?: string | null
}

export interface PatientNameListItem {
  id?: string | null
  name?: PatientNameText[] | null
}

export interface PatientNamesResult {
  PatientList: Array<PatientNameListItem | null>
  count: number
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(String(error))
}

function mapPatientNamesApiItemsToGraphqlShape(
  items: Array<{ id: string; name?: string | null }>
): PatientNamesResult['PatientList'] {
  return items.map((item) => ({
    id: item.id,
    name: item.name ? [{ text: item.name }] : null
  }))
}

async function fetchPatientNamesFromApi(
  params: GetPatientNamesParams = {}
): Promise<PatientNamesResult> {
  // POST is used instead of GET to avoid exposing patient names in URL query parameters.
  const body: Record<string, unknown> = {
    count: params.count ?? DEFAULT_PATIENT_NAME_COUNT,
    offset: params.offset ?? 0
  }
  if (params.caseManagerId) body.case_manager_id = params.caseManagerId
  if (params.addressState?.length) body.state = params.addressState.map((s) => s.toUpperCase())
  if (params.name) body.name = params.name
  if (params.includeTestIds) body.include_test_ids = true

  const response = await new HttpClient().fetch(`${BASE_API_URL}/patients/names`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  if (!response.ok) throw new Error(`REST /patients/names failed with status ${response.status}`)

  const json = (await response.json()) as {
    items?: Array<{ id: string; name?: string | null }>
    count?: number
  }

  const PatientList = mapPatientNamesApiItemsToGraphqlShape(json.items ?? [])
  // GraphQL GET_PATIENT_NAMES exposes only the returned list (no total count), so keep
  // comparison parity by using returned item length as count for both paths.
  return { PatientList, count: PatientList.length }
}

async function fetchPatientNamesFromGraphql(medplum: MedplumClient): Promise<PatientNamesResult> {
  const { data, errors } = await medplum.graphql(GET_PATIENT_NAMES, null, {})
  if (errors?.length) {
    throw new Error(`GraphQL GET_PATIENT_NAMES error: ${errors[0].message}`)
  }
  const PatientList =
    (data as { PatientList?: Array<PatientNameListItem | null> | null } | null)?.PatientList ?? []
  return { PatientList, count: PatientList.length }
}

function hasPatientNamesResultMismatch(
  graphql: PatientNamesResult,
  rest: PatientNamesResult,
  params?: GetPatientNamesParams
): boolean {
  const differences: string[] = []

  if (graphql.count !== rest.count) {
    differences.push(`count mismatch: GraphQL=${graphql.count}, REST=${rest.count}`)
  }

  const graphqlIds = graphql.PatientList.map((item) => item?.id ?? null).filter(
    (id): id is string => Boolean(id)
  )
  const restIds = rest.PatientList.map((item) => item?.id ?? null).filter((id): id is string =>
    Boolean(id)
  )

  if (graphqlIds.length !== restIds.length) {
    differences.push(`item count mismatch: GraphQL=${graphqlIds.length}, REST=${restIds.length}`)
  }

  const minLen = Math.min(graphqlIds.length, restIds.length)
  for (let i = 0; i < minLen; i++) {
    if (graphqlIds[i] !== restIds[i]) {
      differences.push(`id mismatch at index ${i}: GraphQL=${graphqlIds[i]}, REST=${restIds[i]}`)
    }
  }

  const graphqlSet = new Set(graphqlIds)
  const restSet = new Set(restIds)
  const onlyInGraphql = graphqlIds.filter((id) => !restSet.has(id))
  const onlyInRest = restIds.filter((id) => !graphqlSet.has(id))

  if (onlyInGraphql.length) {
    differences.push(`patients only in GraphQL: ${onlyInGraphql.join(', ')}`)
  }
  if (onlyInRest.length) {
    differences.push(`patients only in REST: ${onlyInRest.join(', ')}`)
  }

  if (differences.length) {
    captureException(
      new Error('[PatientFetchService] GraphQL vs REST mismatch GET_PATIENT_NAMES'),
      {
        tags: { source: SentrySources.PATIENT_FETCH },
        extras: {
          params,
          info: {
            type: 'graphql-rest-mismatch',
            differences,
            graphqlIds,
            restIds,
            graphqlCount: graphql.count,
            restCount: rest.count
          }
        }
      }
    )
    return true
  }

  return false
}

export async function getPatientNames(
  params: GetPatientNamesParams = {},
  patientNameQueriesLowImpact = false,
  medplum: MedplumClient = defaultMedplum
): Promise<PatientNamesResult> {
  if (!patientNameQueriesLowImpact) return fetchPatientNamesFromGraphql(medplum)

  const [graphqlOutcome, restOutcome] = await Promise.allSettled([
    fetchPatientNamesFromGraphql(medplum),
    fetchPatientNamesFromApi(params)
  ])

  if (restOutcome.status === 'rejected') {
    captureException(normalizeError(restOutcome.reason), {
      tags: { source: SentrySources.PATIENT_FETCH },
      extras: { params }
    })
  }

  if (graphqlOutcome.status === 'rejected') throw graphqlOutcome.reason

  if (restOutcome.status === 'fulfilled') {
    const hasMismatch = hasPatientNamesResultMismatch(
      graphqlOutcome.value,
      restOutcome.value,
      params
    )
    return hasMismatch ? graphqlOutcome.value : restOutcome.value
  }

  return graphqlOutcome.value
}

function normalizePatientId(idOrRef: string): string {
  if (!idOrRef.includes('/')) return idOrRef
  const [, id] = idOrRef.split('/', 2)
  return id || idOrRef
}

function normalizeApiPatient(resource: unknown): Patient | null {
  if (!resource || typeof resource !== 'object') return null
  const patient = resource as Patient
  if (!patient.id) return null
  return {
    ...patient,
    resourceType: 'Patient'
  }
}

// --- REST fetchers ---

async function fetchPatientListFromApi(
  params: GetPatientConnectionParams
): Promise<PatientConnectionResult> {
  // Minimal patient list contract for getPatientConnection consumers (Patients table):
  // - Core patient fields from BasePatient (id/name/identifier/address/active)
  // - CareTeamList (for caregiver / case manager display)
  // - serviceType + admissionStatusExtension (for service type + admission status columns)
  // Keep includes narrow to CareTeam for list performance; backend derives extension-based
  // aliases (serviceType/admissionStatusExtension) directly from Patient extensions.
  const body: Record<string, unknown> = {
    count: params.count,
    offset: params.offset,
    includes_all: false,
    includes: ['CareTeam'],
    include_test_ids: params.includeTestIds ?? false
  }
  if (params.caseManagerId) body.case_manager_id = params.caseManagerId
  if (params.addressState?.length) body.state = params.addressState.map((s) => s.toUpperCase())
  if (params.name) body.name = params.name
  if (params.linkedStatus) body.linked_status = params.linkedStatus
  if (params.linkedStatus && params.cursor) body.cursor = params.cursor

  const response = await new HttpClient().fetch(`${BASE_API_URL}/patients/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  if (!response.ok)
    throw new HttpResponseError(
      response.status,
      `REST /patients/search failed with status ${response.status}`
    )

  const json = await response.json()
  const edges: PatientConnectionResult['edges'] =
    json.edges?.map((edge: { resource: PatientData_PatientCareDataFragment }) => ({
      resource: edge.resource
    })) ?? []

  return { edges, count: json.count ?? 0, nextCursor: json.next_cursor ?? null }
}

async function fetchPatientResourcesFromApi(
  body: Record<string, unknown>,
  fallbackCount = 0
): Promise<{ patients: Patient[]; count: number }> {
  const response = await new HttpClient().fetch(`${BASE_API_URL}/patients/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  if (!response.ok)
    throw new HttpResponseError(
      response.status,
      `REST /patients/search failed with status ${response.status}`
    )
  const json = await response.json()
  const patients = (json.edges ?? [])
    .map((edge: { resource?: unknown }) => normalizeApiPatient(edge.resource))
    .filter((patient: Patient | null): patient is Patient => !!patient)
  return { patients, count: json.count ?? fallbackCount }
}

async function searchPatientsByNameFromApi(query: string): Promise<Patient[]> {
  const trimmed = query.trim()
  if (!trimmed) return []
  const { patients } = await fetchPatientResourcesFromApi({ count: 100, offset: 0, name: trimmed })
  return patients
}

// --- Public pure functions ---

export async function getPatientConnection(
  params: GetPatientConnectionParams
): Promise<PatientConnectionResult> {
  return fetchPatientListFromApi(params)
}

export async function searchPatientsByName(query: string): Promise<Patient[]> {
  return searchPatientsByNameFromApi(query)
}

export async function getTestPatientIds(): Promise<string[]> {
  const { patients } = await fetchPatientResourcesFromApi({
    count: 1000,
    offset: 0,
    test_resource: true
  })
  return patients.map((patient) => patient.id).filter((id): id is string => !!id)
}

async function fetchPatientsByIdsFromApi(normalizedIds: string[]): Promise<Patient[]> {
  const { patients } = await fetchPatientResourcesFromApi(
    { count: 1000, offset: 0, test_resource: false, patient_ids: normalizedIds },
    normalizedIds.length
  )
  return patients
}

export async function getPatientsByIds(ids: string[]): Promise<Patient[]> {
  const normalizedIds = Array.from(
    new Set(ids.map((id) => normalizePatientId(id).trim()).filter((id) => id.length > 0))
  )
  if (!normalizedIds.length) return []
  return fetchPatientsByIdsFromApi(normalizedIds)
}
